import React from 'react';
import PropTypes from 'prop-types';
import {
  seedGraceFor,
  maxGraceFor,
  graceCeilingError,
  unitKey,
  parseNonNegativeInt,
} from 'helpers/recurrenceLimits';

// recurrence.graceIsCustom is the single source of truth for "the user typed this" versus "this
// is the live suggestion": a value comparison would misfire the moment a deliberate choice equals
// the seed and could never tell "never configured" from "configured to exactly the seed". While
// not customised the seed is derived fresh on every render purely for DISPLAY and never written;
// persisting a re-seeded value on a real cadence edit lives in Recurrence.js's reconcileGrace.
// This component has no effect of its own (a mount-time onChange dirtied the whole form once).

var EarlySubmissionEditor = function (props) {
  var graceInterval = props.graceInterval;
  var graceIsCustom = props.graceIsCustom;
  var onChange = props.onChange;
  var t = props.t;
  var errors = props.errors || {};

  // The props carry the same keys the limits read off a recurrence, so they are the rule.
  var seed = seedGraceFor(props);
  var max = maxGraceFor(props);
  // The ceiling error shows live while typing, from the same check Save runs
  // (this editor only renders in schedule mode, which the check keys on); an
  // API or Save-time error for the field takes precedence when present.
  var intervalError = errors.scheduledCompletionGraceInterval
    || graceCeilingError(Object.assign({ mode: 'schedule' }, props), t);

  var displayInterval = graceIsCustom
    ? (graceInterval === null || graceInterval === undefined ? null : graceInterval)
    : seed.value;
  var showUseSuggested = !!graceIsCustom && displayInterval !== seed.value;
  var suffixCount = displayInterval === null || displayInterval === undefined ? 0 : displayInterval;

  var handleIntervalChange = function (e) {
    var value = parseNonNegativeInt(e.target.value);
    if (value === null) {
      onChange({ graceInterval: null, graceUnit: seed.unit, graceIsCustom: true });
      return;
    }
    // Deliberately not clamped to max.value: clamping on every keystroke makes a value above the
    // ceiling impossible to type and silently rewrites it instead of showing the validator's
    // error (errors.scheduledCompletionGraceInterval).
    onChange({ graceInterval: value, graceUnit: seed.unit, graceIsCustom: true });
  };

  var handleUseSuggested = function () {
    onChange({ graceInterval: seed.value, graceUnit: seed.unit, graceIsCustom: false });
  };

  return (
    <div className="inspect-recurrence-grid-row">
      <label className="inspect-recurrence-grid-label" htmlFor="inspect-early-window">
        {t('inspect:earlyWindowLabel')}
      </label>
      <div className="inspect-recurrence-grid-control">
        <div className="inspect-recurrence-row">
          <span className="inspect-recurrence-every-label">{t('inspect:earlyWindowUpTo')}</span>
          <input
            id="inspect-early-window"
            type="number"
            className={'inspect-recurrence-number' + (intervalError ? ' has-error' : '')}
            value={displayInterval === null || displayInterval === undefined ? '' : displayInterval}
            min="0"
            max={max.value}
            onChange={handleIntervalChange}
            aria-label={t('inspect:earlyWindowLabel')}
          />
          <span className="inspect-recurrence-every-label">
            {t('inspect:earlyWindowSuffix', { unit: t(unitKey(seed.unit, suffixCount)) })}
          </span>
          {showUseSuggested && (
            <button type="button" className="inspect-inline-link" onClick={handleUseSuggested}>
              {t('inspect:earlyWindowUseSuggested', { seed: seed.value })}
            </button>
          )}
        </div>
        {intervalError && (
          <div className="slate-text-input-error">{intervalError}</div>
        )}
        {errors.scheduledCompletionGraceUnit && (
          <div className="slate-text-input-error">{errors.scheduledCompletionGraceUnit}</div>
        )}
        <p className="inspect-mode-hint inspect-recurrence-grid-hint">
          {t('inspect:earlyWindowHint', { max: max.value })}
        </p>
      </div>
    </div>
  );
};

EarlySubmissionEditor.propTypes = {
  frequency: PropTypes.string,
  interval: PropTypes.number,
  hoursOfDay: PropTypes.array,
  daysOfWeek: PropTypes.array,
  daysOfMonth: PropTypes.array,
  graceInterval: PropTypes.number,
  graceIsCustom: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  errors: PropTypes.object,
};

export default EarlySubmissionEditor;
